import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { hrms, it, claims as claimsApi, pm } from '../../services/api';
import { getFileUrl } from '../../services/api';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import StatCard from '../../components/common/StatCard';
import CheckInOutWidget from '../../components/common/CheckInOutWidget';
import { useAuth } from '../../contexts/AuthContext';
import { theme } from '../../theme';

const TASK_P_COLOR = { urgent: '#DC2626', critical: '#DC2626', high: '#D97706', medium: '#D97706', low: '#059669', none: '#6B7280' };
const TASK_P_BG    = { urgent: '#FFF1F2', critical: '#FFF1F2', high: '#FFF7ED', medium: '#FFFBEB', low: '#F0FDF4',  none: '#F9FAFB' };
const TASK_P_ORDER = { urgent: 0, critical: 0, high: 1, medium: 2, low: 3, none: 4 };

function taskDaysDiff(d) {
  if (!d) return null;
  return Math.ceil((new Date(d) - new Date(new Date().toDateString())) / 86400000);
}

function EmployeeDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [myTickets, setMyTickets] = useState([]);
  const [myClaims, setMyClaims] = useState([]);
  const [hallOfFame, setHallOfFame] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingActions, setPendingActions] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [wishedToday, setWishedToday] = useState(new Set());
  const [wishingSending, setWishingSending] = useState(null);

  useEffect(() => {
    Promise.allSettled([
      hrms.getAnnouncements(),
      hrms.getTodayBirthdays(),
      hrms.getMyLeaveBalances(),
      it.getMyTickets(),
      claimsApi.getMyClaims(),
      hrms.getHallOfFame(),
      pm.getMyIssues(),
      hrms.getMyWishesSentToday(),
    ]).then(([ar, br, lb, tr, cr, hofr, taskr, wishedRes]) => {
      if (ar.status === 'fulfilled') setAnnouncements((ar.value.data || []).slice(0, 5));
      if (br.status === 'fulfilled') setBirthdays(br.value.data || []);
      if (lb.status === 'fulfilled') setLeaveBalances(lb.value.data || []);
      if (tr.status === 'fulfilled') { const d = tr.value.data; setMyTickets((Array.isArray(d) ? d : d.items || []).slice(0, 5)); }
      if (cr.status === 'fulfilled') { const d = cr.value.data; setMyClaims((Array.isArray(d) ? d : d.items || []).filter((c) => c.status === 'submitted' || c.status === 'pending').slice(0, 5)); }
      if (hofr.status  === 'fulfilled') setHallOfFame((hofr.value.data || []).slice(0, 10));
      if (wishedRes.status === 'fulfilled') setWishedToday(new Set(wishedRes.value.data || []));
      if (taskr.status === 'fulfilled') {
        const all = (taskr.value.data || []).filter(t => t.status !== 'completed' && t.status !== 'cancelled');
        const sorted = all.sort((a, b) => {
          const da = a.due_date ? taskDaysDiff(a.due_date) : 9999;
          const db_ = b.due_date ? taskDaysDiff(b.due_date) : 9999;
          if (da !== db_) return da - db_;
          return (TASK_P_ORDER[a.priority] ?? 4) - (TASK_P_ORDER[b.priority] ?? 4);
        });
        setMyTasks(sorted.slice(0, 5));
      }

      const actions = [];
      if (user?.profile_status === 'rejected') {
        actions.push({
          id: 'profile_rejected',
          label: 'Your profile was rejected by HR — click to edit and resubmit',
          subLabel: user.profile_rejection_reason,
          path: '/employee/complete-profile',
          color: '#DC2626',
          icon: '❌',
        });
      } else if (!user?.is_profile_complete) {
        actions.push({ id: 'profile', label: 'Complete your personal profile', path: '/employee/complete-profile', color: '#e53e3e', icon: '👤' });
      }
      setPendingActions(actions);
      setLoading(false);
    });
  }, [user]);

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good Morning';
    if (hour < 18) return 'Good Afternoon';
    return 'Good Evening';
  };

  const handleWish = async (birthday) => {
    if (wishedToday.has(birthday.id)) return;
    setWishingSending(birthday.id);
    try {
      const autoMsg = `Wishing you a very Happy Birthday, ${birthday.full_name.split(' ')[0]}! 🎂 May this special day bring you joy, happiness, and success. Many happy returns of the day!`;
      await hrms.sendBirthdayWish(birthday.id, autoMsg);
      setWishedToday(prev => new Set([...prev, birthday.id]));
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to send wish.');
    } finally {
      setWishingSending(null);
    }
  };

  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Welcome Banner */}
      <div style={{ background: `linear-gradient(135deg, ${theme.dark} 0%, ${theme.darkSecondary} 60%, #3D1A00 100%)`, borderRadius: '0.75rem', padding: '1.75rem 2rem', color: theme.white, position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', right: '-20px', top: '-30px', width: '180px', height: '180px', borderRadius: '50%', background: `${theme.primary}15` }} />
        <div style={{ position: 'absolute', right: '40px', bottom: '-40px', width: '120px', height: '120px', borderRadius: '50%', background: `${theme.primary}10` }} />
        <div style={{ position: 'relative' }}>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 700, margin: '0 0 0.375rem' }}>
            {greeting()}, {user?.full_name?.split(' ')[0] || user?.username}! 👋
          </h1>
          <p style={{ color: '#9CA3AF', margin: 0, fontSize: '0.875rem' }}>{today}</p>
        </div>
      </div>

      {/* Check In / Check Out */}
      <CheckInOutWidget />

      {/* ── Pending Actions ── */}
      {pendingActions.length > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '0.75rem', padding: '1rem 1.25rem' }}>
          <h2 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#92400e', margin: '0 0 0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            ⚠️ Action Required — Please complete the following to finish onboarding
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {pendingActions.map(action => (
              <button key={action.id} onClick={() => navigate(action.path)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', background: '#fff', border: `1.5px solid ${action.color}30`, borderLeft: `4px solid ${action.color}`, borderRadius: '0.5rem', padding: '0.75rem 1rem', cursor: 'pointer', textAlign: 'left', width: '100%', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fef9c3'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                <span style={{ fontSize: '1.25rem', flexShrink: 0 }}>{action.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>{action.label}</div>
                  {action.subLabel && (
                    <div style={{ fontSize: '0.78rem', color: '#DC2626', marginTop: '0.2rem', fontStyle: 'italic' }}>
                      Reason: {action.subLabel}
                    </div>
                  )}
                </div>
                <span style={{ marginLeft: 'auto', fontSize: '0.8rem', color: action.color, fontWeight: 600, flexShrink: 0 }}>
                  {action.id === 'profile_rejected' ? 'Edit & Resubmit →' : 'Complete →'}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Leave Balances */}
      {leaveBalances.length > 0 && (
        <div>
          <h2 style={{ fontSize: '1rem', fontWeight: 700, color: theme.text, margin: '0 0 0.875rem' }}>Leave Balances</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '0.875rem' }}>
            {leaveBalances.map((lb, i) => (
              <div key={i} style={{ background: theme.white, borderRadius: '0.75rem', padding: '1rem', border: `1px solid ${theme.border}`, borderTop: `3px solid ${[theme.primary, theme.info, theme.success, theme.warning, '#8B5CF6', '#EC4899'][i % 6]}`, textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <p style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>{lb.balance ?? lb.remaining ?? '—'}</p>
                <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0.25rem 0 0' }}>{lb.leave_type_name || lb.leave_type?.name}</p>
                <p style={{ fontSize: '0.65rem', color: theme.textMuted, margin: '0.1rem 0 0' }}>days remaining</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Two columns */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* Announcements */}
        <Card title="Latest Announcements">
          {announcements.length === 0 ? (
            <p style={{ color: theme.textMuted, textAlign: 'center', padding: '1.5rem 0' }}>No announcements</p>
          ) : (
            <>
              <style>{`
                @keyframes announcement-scroll {
                  0%       { transform: translateY(0); }
                  100%     { transform: translateY(-50%); }
                }
                .announcement-track {
                  animation: announcement-scroll ${announcements.length * 3}s linear infinite;
                }
                .announcement-track:hover { animation-play-state: paused; }
              `}</style>
              {/* Fixed window — exactly 3 items tall */}
              <div style={{ height: '222px', overflow: 'hidden', position: 'relative' }}>
                {/* Scrolling track — duplicate list for seamless loop */}
                <div className={announcements.length > 3 ? 'announcement-track' : ''}>
                  {(announcements.length > 3
                    ? [...announcements, ...announcements]  // duplicate for seamless loop
                    : announcements
                  ).map((ann, i) => (
                    <div key={i} style={{ padding: '0.75rem 0', borderBottom: `1px solid ${theme.border}` }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.625rem' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: ann.priority === 'high' ? theme.danger : theme.primary, flexShrink: 0, marginTop: '6px' }} />
                        <div style={{ minWidth: 0 }}>
                          <p style={{ fontWeight: 600, fontSize: '0.875rem', margin: '0 0 0.25rem', color: theme.text }}>{ann.title}</p>
                          <p style={{ fontSize: '0.8rem', color: theme.gray, margin: 0, lineHeight: 1.55, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                            {ann.content}
                          </p>
                          <p style={{ fontSize: '0.7rem', color: theme.textMuted, margin: '0.25rem 0 0' }}>
                            {ann.created_at ? new Date(ann.created_at).toLocaleDateString() : ''}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </Card>

        {/* Birthdays */}
        <Card title="Today's Birthdays 🎂">
          {birthdays.length === 0 ? (
            <p style={{ color: theme.textMuted, textAlign: 'center', padding: '1.5rem 0' }}>No birthdays today</p>
          ) : (
            <>
              <style>{`
                @keyframes birthday-scroll {
                  0%   { transform: translateY(0); }
                  100% { transform: translateY(-50%); }
                }
                .birthday-track {
                  animation: birthday-scroll ${birthdays.length * 3}s linear infinite;
                }
                .birthday-track:hover { animation-play-state: paused; }
              `}</style>
              <div style={{ height: '222px', overflow: 'hidden', position: 'relative' }}>
                <div className={birthdays.length > 3 ? 'birthday-track' : ''}>
                  {(birthdays.length > 3 ? [...birthdays, ...birthdays] : birthdays).map((b, i) => (
                    <div key={i} style={{ padding: '0.75rem 0', borderBottom: `1px solid ${theme.border}` }}>
                      {/* Top row: photo + name + wish button */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        {/* Profile photo */}
                        <div style={{ position: 'relative', flexShrink: 0 }}>
                          {b.profile_photo ? (
                            <img
                              src={getFileUrl(b.profile_photo)}
                              alt={b.full_name}
                              style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: '2px solid #FDE68A' }}
                              onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                            />
                          ) : null}
                          <div style={{
                            width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg, #FEF3C7, #FDE68A)',
                            display: b.profile_photo ? 'none' : 'flex',
                            alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0,
                            border: '2px solid #FDE68A',
                          }}>🎂</div>
                          {/* Birthday badge */}
                          <span style={{ position: 'absolute', bottom: -2, right: -2, fontSize: '0.75rem' }}>🎉</span>
                        </div>
                        {/* Name + dept */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontWeight: 700, fontSize: '0.875rem', margin: 0, color: theme.text }}>{b.full_name}</p>
                          <p style={{ fontSize: '0.72rem', color: theme.textMuted, margin: '0.1rem 0 0' }}>{b.department || b.position_name || ''}</p>
                        </div>
                        {/* Wish button — hide for own birthday */}
                        {b.id !== user?.emp_id && (
                          <button
                            onClick={() => handleWish(b)}
                            disabled={wishedToday.has(b.id) || wishingSending === b.id}
                            style={{
                              padding: '0.35rem 0.875rem',
                              background: wishedToday.has(b.id) ? '#D1FAE5' : 'linear-gradient(135deg, #F59E0B, #EF4444)',
                              color: wishedToday.has(b.id) ? '#065F46' : '#fff',
                              border: 'none', borderRadius: '999px', cursor: wishedToday.has(b.id) ? 'default' : 'pointer',
                              fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap',
                              opacity: wishingSending === b.id ? 0.6 : 1,
                              transition: 'all 0.2s',
                            }}>
                            {wishingSending === b.id ? '⏳' : wishedToday.has(b.id) ? '✓ Wished!' : '🎉 Wish'}
                          </button>
                        )}
                      </div>
                      {/* Auto-generated wish message */}
                      <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', background: 'linear-gradient(135deg, #FEF3C7, #FFF7ED)', borderRadius: '0.5rem', borderLeft: '3px solid #F59E0B' }}>
                        <p style={{ margin: 0, fontSize: '0.75rem', color: '#92400E', fontStyle: 'italic', lineHeight: 1.4 }}>
                          🎂 Wishing {b.full_name.split(' ')[0]} a very Happy Birthday! May this special day bring joy, happiness, and success. Many happy returns! 🎉
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Hall of Fame */}
      {hallOfFame.length > 0 && (
        <Card title="🏆 Hall of Fame">
          <style>{`
            @keyframes hof-scroll {
              0%   { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .hof-track {
              animation: hof-scroll ${hallOfFame.length * 4}s linear infinite;
            }
            .hof-track:hover { animation-play-state: paused; }
          `}</style>
          <div style={{ overflow: 'hidden', position: 'relative' }}>
            <div className={hallOfFame.length > 2 ? 'hof-track' : ''} style={{ display: 'flex', gap: '1rem', width: 'max-content' }}>
              {(hallOfFame.length > 2 ? [...hallOfFame, ...hallOfFame] : hallOfFame).map((entry, i) => (
                <div key={i} style={{ width: '260px', flexShrink: 0, background: theme.bgSecondary, border: `1px solid ${theme.border}`, borderRadius: '0.75rem', overflow: 'hidden' }}>
                  {entry.image_path ? (
                    <img src={`http://localhost:8000/${entry.image_path}`} alt={entry.title} style={{ width: '100%', height: '100px', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ height: '60px', background: `linear-gradient(135deg, ${theme.dark} 0%, #1a3a5c 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.75rem' }}>🏆</div>
                  )}
                  <div style={{ padding: '0.75rem' }}>
                    <p style={{ margin: '0 0 0.35rem', fontWeight: 700, fontSize: '0.85rem', color: theme.text }}>{entry.title}</p>
                    {entry.achievement_date && (
                      <p style={{ margin: '0 0 0.5rem', fontSize: '0.7rem', color: theme.textMuted }}>
                        {new Date(entry.achievement_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    )}
                    {entry.honorees?.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                        {entry.honorees.slice(0, 3).map(h => (
                          <div key={h.employee_id} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                            {h.profile_photo ? (
                              <img src={`http://localhost:8000/${h.profile_photo}`} alt={h.full_name} style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', border: '1.5px solid #FEF3C7' }} onError={e => { e.target.style.display = 'none'; }} />
                            ) : (
                              <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem' }}>🏅</div>
                            )}
                            <span style={{ fontSize: '0.72rem', color: theme.text, fontWeight: 500 }}>{h.full_name}</span>
                          </div>
                        ))}
                        {entry.honorees.length > 3 && <span style={{ fontSize: '0.7rem', color: theme.textMuted }}>+{entry.honorees.length - 3} more</span>}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* ── My Pending Tasks ── */}
      <div style={{ background: theme.white, border: `1px solid ${theme.border}`, borderRadius: '0.875rem', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.875rem 1.25rem', borderBottom: `1px solid ${theme.border}`, background: '#F8FAFC' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.1rem' }}>🎯</span>
            <span style={{ fontWeight: 700, fontSize: '0.95rem', color: theme.text }}>My Pending Tasks</span>
            {myTasks.length > 0 && (
              <span style={{ background: theme.primary, color: '#fff', borderRadius: '999px', padding: '1px 8px', fontSize: '0.7rem', fontWeight: 700 }}>{myTasks.length}</span>
            )}
          </div>
          <button onClick={() => navigate('/my-tasks')}
            style={{ fontSize: '0.78rem', fontWeight: 600, color: theme.primary, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            View all →
          </button>
        </div>

        {myTasks.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: theme.textMuted, fontSize: '0.85rem' }}>
            🎉 No pending tasks — you're all caught up!
          </div>
        ) : (
          myTasks.map((task, idx) => {
            const pColor = TASK_P_COLOR[task.priority] || TASK_P_COLOR.none;
            const pBg    = TASK_P_BG[task.priority]    || TASK_P_BG.none;
            const diff   = taskDaysDiff(task.due_date);
            const isLast = idx === myTasks.length - 1;

            let dueBadge = null;
            if (diff !== null) {
              if (diff < 0)   dueBadge = { text: `${Math.abs(diff)}d overdue`, bg: '#FFF1F2', color: '#DC2626' };
              else if (diff === 0) dueBadge = { text: 'Due today',   bg: '#FFFBEB', color: '#B45309' };
              else if (diff <= 3)  dueBadge = { text: `${diff}d left`, bg: '#FFF7ED', color: '#D97706' };
              else                 dueBadge = { text: new Date(task.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }), bg: '#F3F4F6', color: '#6B7280' };
            }

            return (
              <div key={task.id}
                onClick={() => task.project_id && navigate(`/projects/${task.project_id}`)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.7rem 1.25rem', borderBottom: isLast ? 'none' : `1px solid #F1F5F9`, cursor: task.project_id ? 'pointer' : 'default', transition: 'background 0.1s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                {/* Priority stripe */}
                <div style={{ width: 3, height: 32, borderRadius: 2, background: pColor, flexShrink: 0 }} />

                {/* State dot */}
                <div style={{ width: 9, height: 9, borderRadius: '50%', background: task.state_color || pColor, flexShrink: 0, boxShadow: `0 0 0 2px ${(task.state_color || pColor) + '30'}` }} />

                {/* Title + project */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: '0.84rem', fontWeight: 600, color: theme.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{task.title}</p>
                  {task.project_name && (
                    <p style={{ margin: '0.1rem 0 0', fontSize: '0.7rem', color: theme.textMuted }}>🗂 {task.project_name}</p>
                  )}
                </div>

                {/* Priority badge */}
                <span style={{ background: pBg, color: pColor, padding: '2px 7px', borderRadius: 9999, fontSize: '0.68rem', fontWeight: 700, flexShrink: 0 }}>
                  {task.priority || 'none'}
                </span>

                {/* Due badge */}
                {dueBadge && (
                  <span style={{ background: dueBadge.bg, color: dueBadge.color, padding: '2px 7px', borderRadius: 9999, fontSize: '0.68rem', fontWeight: 700, flexShrink: 0 }}>
                    {dueBadge.text}
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* Recent Tickets */}
        <Card title="My Recent Tickets">
          {myTickets.length === 0 ? (
            <p style={{ color: theme.textMuted, textAlign: 'center', padding: '1.5rem 0' }}>No tickets raised</p>
          ) : myTickets.map((t, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.625rem 0', borderBottom: i < myTickets.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
              <div>
                <p style={{ fontWeight: 600, fontSize: '0.875rem', margin: 0 }}>{t.title}</p>
                <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0.15rem 0 0' }}>{t.category} · {t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</p>
              </div>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <Badge status={t.priority} />
                <Badge status={t.status} />
              </div>
            </div>
          ))}
        </Card>

        {/* Pending Claims */}
        <Card title="Pending Claims">
          {myClaims.length === 0 ? (
            <p style={{ color: theme.textMuted, textAlign: 'center', padding: '1.5rem 0' }}>No pending claims</p>
          ) : myClaims.map((c, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.625rem 0', borderBottom: i < myClaims.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
              <div>
                <p style={{ fontWeight: 600, fontSize: '0.875rem', margin: 0 }}>{c.title}</p>
                <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0.15rem 0 0' }}>₹{Number(c.total_amount || 0).toLocaleString()}</p>
              </div>
              <Badge status={c.status} />
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

export default EmployeeDashboard;
